// src/stores/authStore.ts (Fixed version)
import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { authHelpers, JWTClaims } from '../lib/authHelpers'
import { UserProfile } from '../types'

interface AuthState {
  user: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean // Add this to track if auth has been initialized
  error: string | null
  claims: JWTClaims | null

  // Actions
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  initialize: () => Promise<void>
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>
  refreshClaims: () => Promise<void>
  refreshUser: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false, // Start as false instead of true
  isInitialized: false,
  error: null,
  claims: null,

  clearError: () => set({ error: null }),

  signIn: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null })

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      if (data.user) {
        // Initialize claims after successful login
        await authHelpers.initializeUserClaims(data.user.id)

        // Get updated profile with claims
        const profile = await authHelpers.getUserProfileWithClaims()
        const claims = await authHelpers.getUserClaims()

        set({
          user: profile,
          isAuthenticated: true,
          isLoading: false,
          claims,
          isInitialized: true,
        })
      }
    } catch (error: unknown) {
      console.error('Sign in error:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to sign in',
        isLoading: false,
        isAuthenticated: false,
        user: null,
        claims: null,
      })
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null })

      const { error } = await supabase.auth.signOut()

      if (error) throw error

      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        claims: null,
        isInitialized: true, // Keep initialized as true
      })
    } catch (error: unknown) {
      console.error('Sign out error:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to sign out',
        isLoading: false,
      })
    }
  },

  initialize: async () => {
    // Prevent multiple initializations
    if (get().isInitialized) {
      return
    }

    try {
      set({ isLoading: true, error: null })

      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session?.user) {
        // Ensure claims are set
        await authHelpers.initializeUserClaims(session.user.id)

        // Get profile and claims
        const profile = await authHelpers.getUserProfileWithClaims()
        const claims = await authHelpers.getUserClaims()

        set({
          user: profile,
          isAuthenticated: true,
          isLoading: false,
          claims,
          isInitialized: true,
        })
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          claims: null,
          isInitialized: true,
        })
      }

      // Set up auth state change listener (only once)
      if (!get().isInitialized) {
        supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('Auth state change:', event, session?.user?.email)

          if (session?.user) {
            // User signed in, ensure claims and get profile
            await authHelpers.initializeUserClaims(session.user.id)
            const profile = await authHelpers.getUserProfileWithClaims()
            const claims = await authHelpers.getUserClaims()

            set({
              user: profile,
              isAuthenticated: true,
              isLoading: false,
              claims,
              isInitialized: true,
            })
          } else if (event === 'SIGNED_OUT') {
            // User signed out
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              claims: null,
              isInitialized: true,
            })
          }
        })
      }
    } catch (error: unknown) {
      console.error('Auth initialization error:', error)
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error:
          error instanceof Error ? error.message : 'Failed to initialize auth',
        claims: null,
        isInitialized: true,
      })
    }
  },

  updateUserProfile: async (updates: Partial<UserProfile>) => {
    try {
      const currentUser = get().user
      if (!currentUser) throw new Error('No authenticated user')

      // Update user profile in database
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', currentUser.id)
        .select()
        .single()

      if (error) throw error

      // If role or organization changed, update claims
      if (updates.role || updates.organization_id) {
        await authHelpers.initializeUserClaims(currentUser.id)
        const claims = await authHelpers.getUserClaims()
        set({ claims })
      }

      // Update local state
      set({
        user: { ...currentUser, ...updates },
      })
    } catch (error: unknown) {
      console.error('Update profile error:', error)
      set({
        error:
          error instanceof Error ? error.message : 'Failed to update profile',
      })
    }
  },

  refreshClaims: async () => {
    try {
      const currentUser = get().user
      if (!currentUser) return

      await authHelpers.initializeUserClaims(currentUser.id)
      const claims = await authHelpers.getUserClaims()

      set({ claims })
    } catch (error: unknown) {
      console.error('Refresh claims error:', error)
      set({
        error:
          error instanceof Error ? error.message : 'Failed to refresh claims',
      })
    }
  },

  refreshUser: async () => {
    try {
      const currentUser = get().user
      if (!currentUser) return

      // Re-fetch user profile with updated data (including avatar_url)
      const profile = await authHelpers.getUserProfileWithClaims()

      if (profile) {
        set({ user: profile })
      }
    } catch (error: unknown) {
      console.error('Refresh user error:', error)
      set({
        error:
          error instanceof Error ? error.message : 'Failed to refresh user',
      })
    }
  },
}))
